<template>
    <div class="mx-auto w-full">
        <div>
            <!-- Unified Responsive Layout -->
            <div class="space-y-4 mb-6">
                <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                    <!-- Left Section: Search and Filters -->
                    <div class="flex flex-col sm:flex-row gap-3 lg:items-center">
                        <SearchInput
                            :loading="loading"
                            placeholder="Search by issue..."
                            class="w-full sm:w-auto"
                            @search="(input) => (searchQuery = input)" />
                        
                        <Select v-model="selectedType" class="w-full sm:w-[180px]">
                            <SelectTrigger>
                                <SelectValue placeholder="All Types" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All Types</SelectItem>
                                <SelectItem v-for="option in typeOptions" :key="option.value" :value="option.value">
                                    {{ option.label }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        
                        <div class="flex gap-1 items-center">
                            <Checkbox v-model="showUnread" :disabled="loading" />
                            <span class="text-sm">Show only unread</span>
                        </div>
                    </div>
                    
                    <!-- Right Section: Actions -->
                    <div class="flex flex-col sm:flex-row gap-3">
                        <ActionMenu
                            variant="row"
                            :left="true"
                            :show-mark-all="true"
                            :disable-mark-all="!anyUnreadNotifications"
                            :loading="markAllLoading"
                            class="w-full sm:w-auto"
                            @markAll="markAllNotificationsRead()" />
                        <Button
                            v-can-access.permissions="[$Permissions.WRITE_ALERTS]"
                            variant="outline"
                            class="gap-1 w-full sm:w-auto"
                            @click="setAlertConfigurationModalOpenStatus(true)">
                            <Settings class="h-4 w-4" />
                            Alerts Settings
                        </Button>
                    </div>
                </div>
            </div>
            <div class="mt-2">
                <ScrollArea class="w-full border shadow-md rounded-md" :class="(filteredNotifications?.length || 0) <= 10 ? 'h-auto' : 'h-70-dynamic'">
                        <Table>
                            <TableHeader class="sticky top-0 bg-background z-10">
                                <TableRow class="bg-muted/50">
                                    <TableHead class="first:rounded-tl-md">Name</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Issue</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead class="last:rounded-tr-md"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <!-- Skeleton rows when loading -->
                                <template v-if="loading">
                                    <TableRow v-for="n in 10" :key="`skeleton-${n}`">
                                        <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                        <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                                        <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                        <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                        <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                    </TableRow>
                                </template>
                                <!-- Actual data rows -->
                                <template v-else>
                                        <TableRow
                                            v-for="(userNotification, userNotificationIndex) in filteredNotifications"
                                            :key="userNotificationIndex">
                                            <TableCell>
                                                <div class="flex items-center gap-2">
                                                    <span v-if="!userNotification.read" class="flex h-2 w-2 rounded-full bg-primary shrink-0" />
                                                    <Button
                                                        variant="link"
                                                        class="h-auto p-0 text-left justify-start font-medium min-w-0 flex-1"
                                                        :title="userNotification?.payload?.entity_name"
                                                        @click="
                                                            $router.push(
                                                                selectedEntityPath(
                                                                    userNotification?.payload?.entity_id,
                                                                    userNotification?.payload?.entity_type,
                                                                    userNotification?.payload?.entity_name
                                                                )
                                                            )
                                                        ">
                                                        <span class="truncate max-w-[150px] sm:max-w-[200px] md:max-w-[250px] lg:max-w-[300px]" :title="userNotification?.payload?.entity_name">
                                                            {{ userNotification?.payload?.entity_name ?? '-' }}
                                                        </span>
                                                    </Button>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div class="text-xs flex items-center gap-2">
                                                    <div>
                                                        <CloudDownload
                                                            v-if="userNotification?.payload?.entity_type === 'sources'"
                                                            class="h-4 w-4 text-muted-foreground" />
                                                        <CloudUpload
                                                            v-if="userNotification?.payload?.entity_type === 'destinations'"
                                                            class="h-4 w-4 text-muted-foreground" />
                                                        <ArrowUpDown
                                                            v-if="userNotification?.payload?.entity_type === 'pipelines'"
                                                            class="h-4 w-4 text-muted-foreground" />
                                                    </div>
                                                    <div>
                                                        {{
                                                            userNotification?.payload?.entity_type === 'sources'
                                                                ? 'Source'
                                                                : userNotification?.payload?.entity_type === 'destinations'
                                                                  ? 'Destination'
                                                                  : userNotification?.payload?.entity_type === 'pipelines'
                                                                    ? 'Pipeline'
                                                                    : 'Unknown'
                                                        }}
                                                    </div>
                                                </div>
                                            </TableCell>

                                            <TableCell class="cursor-pointer">
                                                <div
                                                    :class="[
                                                        'text-xs rounded-md p-2 cursor-pointer border transition-colors',
                                                        userNotification?.payload?.state === 'RECOVERED' ||
                                                        userNotification?.payload?.state === 'INFO'
                                                            ? 'bg-accent/5 text-accent-foreground hover:bg-accent/10 border-accent/20'
                                                            : userNotification?.payload?.state === 'TRIGGERED' ||
                                                                userNotification?.payload?.state === 'TRIGGERERD'
                                                              ? userNotification?.payload?.action_type
                                                                  ? 'bg-primary/5 text-primary hover:bg-primary/10 border-primary/20'
                                                                  : 'bg-destructive/10 text-destructive hover:bg-destructive/20 border-destructive/30'
                                                              : 'bg-destructive/10 text-destructive hover:bg-destructive/20 border-destructive/30'
                                                    ]"
                                                    @click="() => onViewMessageClick(userNotification?.content)">
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <CheckCircle
                                                            v-if="
                                                                userNotification?.payload?.state === 'RECOVERED' ||
                                                                userNotification?.payload?.state === 'INFO'
                                                            "
                                                            class="h-4 w-4 text-accent-foreground shrink-0" />
                                                        <span
                                                            v-else-if="
                                                                userNotification?.payload?.state === 'TRIGGERED' ||
                                                                userNotification?.payload?.state === 'TRIGGERERD'
                                                            ">
                                                            <Info
                                                                v-if="userNotification?.payload?.action_type"
                                                                class="h-4 w-4 text-primary shrink-0" />
                                                            <AlertTriangle v-else class="h-4 w-4 text-destructive shrink-0" />
                                                        </span>
                                                        <span
                                                            class="truncate flex-1 max-w-[300px]"
                                                            :title="userNotification?.content"
                                                            v-html="sanitizeHtml(userNotification?.content ?? '-')"></span>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div class="text-xs font-semibold">
                                                    {{ userNotification?.createdAt ? get_relative_time(userNotification?.createdAt) : '-' }}
                                                </div>
                                                <div class="text-xs">
                                                    {{ userNotification?.createdAt ? get_formatted_date(userNotification?.createdAt) : '-' }}
                                                </div>
                                            </TableCell>
                                            <TableCell class="sticky right-0 text-sm z-10">
                                                <ActionMenu
                                                    variant="menu"
                                                    :show-delete="userHasAccess([$Permissions.WRITE_ALERTS], authStore.user)"
                                                    :show-read="userHasAccess([$Permissions.WRITE_ALERTS], authStore.user) && !userNotification?.read"
                                                    :show-unread="
                                                        userHasAccess([$Permissions.WRITE_ALERTS], authStore.user) && userNotification?.read
                                                    "
                                                    :loading="loading"
                                                    @delete="onDelete([userNotification?.id])"
                                                    @read="markNotificationAs([userNotification?.id], true)"
                                                    @unread="markNotificationAs([userNotification?.id], false)" />
                                            </TableCell>
                                        </TableRow>
                                        <TableEmpty v-if="!loading && !filteredNotifications?.length" :colspan="5">
                                            <TableEmptyState />
                                        </TableEmpty>
                                    </template>
                                </TableBody>
                            </Table>
                    <ScrollBar orientation="horizontal" />
                    <ScrollBar orientation="vertical" />
                </ScrollArea>
            </div>
            <TablePagination
                v-if="filteredNotifications?.length || page > 0"
                :current-page="page - 1"
                :page-size="userNotificationsResponse?.pageSize || 20"
                :total-items="userNotificationsResponse?.totalCount || filteredNotifications?.length || 0"
                :current-page-items-count="filteredNotifications?.length || 0"
                :has-next="userNotificationsResponse?.hasMore"
                item-type="alerts"
                :loading="loading"
                :show-items-per-page="true"
                :show-total-items="false"
                @page-change="changePage"
                @page-size-change="onPageSizeChange" />
        </div>
    </div>
    <AlertConfigurationModal
        v-show="openAlertConfigurationModal"
        :is-modal-open="openAlertConfigurationModal"
        @update:is-open="setAlertConfigurationModalOpenStatus($event)" />

    <!-- Alert Message Dialog -->
    <LogMessageDialog
        :isOpen="logMessageDialogOpen"
        :title="'Alert Message'"
        :message="selectedLogMessage"
        :isError="false"
        @update:isOpen="logMessageDialogOpen = $event" />
</template>

<script setup>
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import TablePagination from '@/components/TablePagination.vue';
import { ref, onMounted, computed, watch } from 'vue';
import SearchInput from '../../components/global/SearchInput.vue';
import { AlertTriangle, CheckCircle, Info, Settings, CloudDownload, CloudUpload, ArrowUpDown } from 'lucide-vue-next';
import LogMessageDialog from '../../components/LogMessageDialog.vue';
import AlertConfigurationModal from '../../components/AlertIntegrations/AlertConfigurationModal.vue';
import { useNovuStore } from '@/stores/novu.store';
import ActionMenu from '@/components/ActionMenu.vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { get_formatted_date, get_relative_time, userHasAccess, sanitizeHtml } from '../../utils/utils.js';
import { ALERT_FILTERS, ALERT_DEFAULT_FILTER } from '@/utils/constants';
import { useAuthStore } from '@/stores/auth.store';
import { TableEmptyState } from '@/components/ui/table-empty-state';

defineProps({
    isModalOpen: {
        type: Boolean,
        default: false
    }
});

let loading = ref(false);
let markAllLoading = ref(false);
let userNotificationsResponse = ref({ pageSize: 20 });
let page = ref(1);
let authStore = useAuthStore();
let searchQuery = ref(false);
let openAlertConfigurationModal = ref(false);
let logMessageDialogOpen = ref(false);
let selectedLogMessage = ref('');
let novuStore = useNovuStore();
let filters = [
    {
        title: 'Type',
        name: 'type',
        multiple: false,
        defaultIndex: 0,
        dropdownWidth: '170px',
        options: [ALERT_DEFAULT_FILTER, ...ALERT_FILTERS]
    }
];
let checkedFilters = ref({});
let selectedType = ref('All');

const typeOptions = computed(() => {
    return ALERT_FILTERS.map((filter) => ({
        value: filter.value,
        label: filter.displayValue
    }));
});

let showUnread = ref(false); // false = show all, true = show only unread
let alertsQuery = computed(() => {
    let query = {};

    if (showUnread.value) {
        query.read = false;
    }

    if (selectedType.value && selectedType.value !== 'All') {
        query.payload = { entity_type: selectedType.value };
    }

    return query;
});

onMounted(async () => {
    await initializeFilters();
    await loadAlertsPage(page.value, alertsQuery.value);
});

async function initializeFilters() {
    filters.forEach((filter) => {
        checkedFilters.value[filter.name] = [filter.multiple ? ALERT_DEFAULT_FILTER : filter.options[filter.defaultIndex]];
    });
    // Initialize type filter
    selectedType.value = 'All';
}

async function loadAlertsPage(pageNumber = 1, query) {
    if (loading.value) {
        return;
    }

    loading.value = true;

    const pageSize = userNotificationsResponse.value?.pageSize || 20;
    const notifications = await novuStore.fetchNotifications(pageNumber - 1, query, pageSize);
    await novuStore.fetchUnreadCount();

    if (novuStore.userNotifications) {
        const currentPageSize = userNotificationsResponse.value?.pageSize || 20;
        userNotificationsResponse.value = {
            ...novuStore.userNotifications,
            pageSize: currentPageSize
        };
        searchQuery.value = '';
    }
    loading.value = false;
}

watch(
    alertsQuery,
    async (newQuery) => {
        await loadAlertsPage(page.value, newQuery);
    },
    { deep: true }
);

watch(selectedType, async (newType) => {
    page.value = 1;
    await loadAlertsPage(page.value, alertsQuery.value);
});

async function onFilterToggle({ filter, filterOption }) {
}

async function markAllNotificationsRead() {
    markAllLoading.value = true;
    try {
        const marked = await novuStore.markAllNotificationsAsRead();
        if (marked) {
            userNotificationsResponse.value.data.forEach((obj) => (obj.read = true));
        }
    } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
    } finally {
        markAllLoading.value = false;
    }
}
async function markNotificationAs(notificationIds, read) {
    const marked = await novuStore.markNotificationsAs(notificationIds, read);

    if (marked) {
        notificationIds.forEach((notificationId) => {
            userNotificationsResponse.value.data.map((x) => {
                if (x.id == notificationId) {
                    x.read = read;
                }
            });
        });
    }
}

async function onDelete(notificationIds) {
    const deleted = await novuStore.removeNotifications(notificationIds);
    if (deleted) {
        await loadAlertsPage(page.value);
    }
}

function onViewMessageClick(message) {
    selectedLogMessage.value = message;
    logMessageDialogOpen.value = true;
}

function selectedEntityPath(id, type, name) {
    const msg = `"${name}" has since been removed.`;
    switch (type) {
        case 'sources':
            return { name: 'source-details', params: { id }, query: { customMessage: `Source ${msg}` } };
        case 'destinations':
            return { name: 'destination-details', params: { id }, query: { customMessage: `Destination ${msg}` } };
        case 'pipelines':
            return { name: 'pipeline-details', params: { id }, query: { customMessage: `Pipeline ${msg}` } };
        default:
            console.warn('Invalid entity type selected:', type);
            break;
    }
}

function setAlertConfigurationModalOpenStatus(value) {
    openAlertConfigurationModal.value = value;
}

async function changePage(newPage) {
    page.value = newPage + 1;
    await loadAlertsPage(page.value, alertsQuery.value);
}

async function onPageSizeChange(newSize) {
    if (!userNotificationsResponse.value || typeof userNotificationsResponse.value !== 'object') {
        userNotificationsResponse.value = { pageSize: newSize };
    } else {
        userNotificationsResponse.value.pageSize = newSize;
    }

    page.value = 1;
    await loadAlertsPage(page.value, alertsQuery.value);
}

const filteredNotifications = computed(() => {
    if (!loading.value) {
        if (!searchQuery.value) {
            return userNotificationsResponse.value?.data ?? [];
        } else {
            return userNotificationsResponse.value?.data.filter((notification) =>
                notification.payload.entity_name.toLowerCase().includes(searchQuery.value.toLowerCase())
            );
        }
    }

    return [];
});

const anyUnreadNotifications = computed(() => {
    const hasUnread = !!novuStore.userUnreadNotificationsCount;
    return hasUnread;
});
</script>

<style scoped>
</style>
